import React, { useMemo, useRef, useState } from 'react';
import { Box, ClickAwayListener, Paper, Popper, Stack, TextField, Typography } from '@mui/material';
import { HexColorPicker, HexAlphaColorPicker } from 'react-colorful';

function expandShortHex(hex: string): string | null {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toLowerCase();
  }
  return null;
}

function normalizeHex(value: string): string | null {
  const t = value.trim();
  if (/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/i.test(t)) return t.toLowerCase();
  return expandShortHex(t);
}

function safeHex(value: string): string {
  return normalizeHex(value) ?? '#808080';
}

const DEFAULT_PRESETS = [
  '#ffffff', '#000000', '#f44336', '#e91e63', '#9c27b0', '#673ab7',
  '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
  '#795548', '#607d8b', '#00e5ff', '#69f0ae', '#ffea00', '#ff4081',
];

export interface SettingsColorPickerProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  showAlpha?: boolean;
  disabled?: boolean;
  presets?: string[];
  placeholder?: string;
}

const SettingsColorPicker: React.FC<SettingsColorPickerProps> = ({
  label,
  value,
  onChange,
  showAlpha,
  disabled,
  presets = DEFAULT_PRESETS,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const displayHex = useMemo(() => safeHex(value), [value]);

  const Picker = showAlpha ? HexAlphaColorPicker : HexColorPicker;

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary" component="label" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" ref={anchorRef}>
        <Box
          onClick={() => !disabled && setOpen((o) => !o)}
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: displayHex,
            border: '2px solid',
            borderColor: 'divider',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'box-shadow 0.15s',
            '&:hover': disabled ? {} : { boxShadow: '0 0 0 2px rgba(100,180,255,0.4)' },
          }}
        />
        <TextField
          size="small"
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          sx={{
            '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem', py: 0.75 },
          }}
        />
      </Stack>

      <Popper open={open && !disabled} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1500 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper elevation={8} sx={{ p: 1.5, width: 240 }}>
            <Picker color={displayHex} onChange={onChange} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Presets
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {presets.map((hex) => (
                <Box
                  key={hex}
                  onClick={() => onChange(hex)}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '3px',
                    bgcolor: hex,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: hex === value ? 'primary.main' : 'divider',
                    '&:hover': { transform: 'scale(1.2)' },
                    transition: 'transform 0.1s',
                  }}
                />
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

export default SettingsColorPicker;
